import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Send, X } from 'lucide-react';
import adminAssistantService from '../../services/adminAssistantService';
import { useToast } from '../../context/ToastContext';
import { ASSISTANT_ACTION_STATUS } from '../../utils/assistantStatus';
import AssistantSummaryCard from './AssistantSummaryCard';

const isLive = (action) =>
  action && action.status === ASSISTANT_ACTION_STATUS.PENDING && new Date(action.expires_at).getTime() > Date.now();

/** Slide-over chat for the gift assistant. Text only; confirmation happens via the card button. */
const AssistantPanel = ({ onClose }) => {
  const toast = useToast();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [action, setAction] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    adminAssistantService
      .getLatest()
      .then((latest) => {
        if (cancelled) return;
        setConversationId(latest.conversation_id);
        setMessages(latest.messages || []);
        setAction(isLive(latest.pending_action) ? latest.pending_action : null);
      })
      .catch((err) => !cancelled && toast.error(err.message));
    inputRef.current?.focus();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, action, outcome, thinking]);

  const send = async () => {
    const message = draft.trim();
    if (!message || thinking || confirming) return;
    setDraft('');
    setThinking(true);
    setMessages((m) => [...m, { role: 'user', text: message, kind: 'message' }]);
    try {
      const res = await adminAssistantService.chat({ conversationId, message });
      setConversationId(res.conversation_id);
      setMessages((m) => [...m, { role: 'assistant', text: res.text, kind: 'message' }]);
      setAction(res.pending_action || null);
      setOutcome(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setThinking(false);
    }
  };

  const reloadMessages = useCallback(async () => {
    try {
      const latest = await adminAssistantService.getLatest();
      setConversationId(latest.conversation_id);
      setMessages(latest.messages || []);
    } catch (err) {
      toast.error(err.message);
    }
  }, [toast]);

  const confirm = async (actionId) => {
    if (confirming) return;
    setConfirming(true);
    try {
      setOutcome({ actionId, result: await adminAssistantService.confirm(actionId) });
      await reloadMessages();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const cancel = async (actionId) => {
    if (confirming) return;
    setConfirming(true);
    try {
      await adminAssistantService.cancel(actionId);
      setAction(null);
      setOutcome(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setAction(null);
    setOutcome(null);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onKeyDown={onKeyDown}>
      <button type="button" aria-label="Close assistant" className="absolute inset-0 bg-black/30" onClick={onClose} tabIndex={-1} />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Gift assistant"
        className="relative flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-xl"
      >
        <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h2 className="text-base font-medium">Gift assistant</h2>
          <div className="flex items-center gap-1">
            <button type="button" onClick={newChat} className="flex items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-muted">
              <Plus className="h-4 w-4" aria-hidden="true" /> New chat
            </button>
            <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 hover:bg-muted">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" aria-live="polite">
          {messages.map((m, i) =>
            m.kind === 'note' ? (
              <p key={i} className="text-center text-xs text-muted-foreground whitespace-pre-wrap">
                {m.text}
              </p>
            ) : (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <p
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border'
                  }`}
                >
                  {m.text}
                </p>
              </div>
            ),
          )}
          {thinking && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Thinking…
            </p>
          )}
          {action && (
            <AssistantSummaryCard action={action} onConfirm={confirm} onCancel={cancel}
              busy={confirming || thinking} /* a turn in flight may replace this card (R23) */
              result={outcome && outcome.actionId === action.id ? outcome.result : null}
            />
          )}
          <div ref={endRef} />
        </div>

        <form
          className="flex items-end gap-2 border-t border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <label htmlFor="assistant-input" className="sr-only">
            Message the gift assistant
          </label>
          <textarea
            id="assistant-input"
            ref={inputRef}
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Describe the gift to send…"
            className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={thinking || confirming || !draft.trim()}
            className="flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm disabled:opacity-50"
          >
            <Send className="h-4 w-4" aria-hidden="true" /> Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default AssistantPanel;
